import Quill from 'quill';

const Module = Quill.import('core/module');

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

// Quill's own default snow-theme palette (themes/base.js COLORS), kept here so the
// custom dropdown offers the same standard colors editors already know from stock Quill.
const STANDARD_COLORS = [
    '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
    '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
    '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
    '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
    '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466'
];

class ColorPaletteModule extends Module {
    constructor(quill, options) {
        super(quill, options);

        this.quill = quill;
        this.options = options;
        this.dropdown = null;
        this.activeFormat = null;
        this.savedSelection = null;

        const toolbar = quill.getModule('toolbar');
        if (toolbar) {
            toolbar.addHandler('color', () => this.showDropdown('color'));
            toolbar.addHandler('background', () => this.showDropdown('background'));
        }

        this.boundOnMouseDown = this.onMouseDown.bind(this);
        document.addEventListener('mousedown', this.boundOnMouseDown);

        this.quill.on('selection-change', range => {
            if (!range && this.dropdown) this.hideDropdown();
        });
    }

    showDropdown(format) {
        if (this.dropdown && this.activeFormat === format) {
            this.hideDropdown();
            return;
        }
        this.hideDropdown();

        this.quill.focus();
        const selection = this.quill.getSelection();
        if (!selection) return;

        this.savedSelection = selection;
        this.activeFormat = format;

        const currentValue = this.quill.getFormat(selection)[format] || '';

        this.dropdown = document.createElement('div');
        this.dropdown.className = 'ql-color-palette';
        document.body.appendChild(this.dropdown);

        const ciColors = (this.options.ciColors || []).filter(Boolean);
        if (ciColors.length) {
            this.appendSwatchSection(this.options.ciColorsLabel || 'CI colors', ciColors, currentValue, false);
        }

        const standardColors = this.options.standardColors || STANDARD_COLORS;
        this.appendSwatchSection(this.options.standardColorsLabel || 'Standard colors', standardColors, currentValue, true);

        const customLabel = document.createElement('div');
        customLabel.className = 'ql-color-palette-label';
        customLabel.textContent = this.options.customColorLabel || 'Custom color';
        this.dropdown.appendChild(customLabel);

        const customRow = document.createElement('div');
        customRow.className = 'ql-color-palette-custom';

        const hexInput = document.createElement('input');
        hexInput.type = 'text';
        hexInput.className = 'ql-color-palette-hex';
        hexInput.placeholder = '#000000';
        hexInput.spellcheck = false;
        hexInput.value = currentValue;
        const commitHex = () => {
            const value = hexInput.value.trim();
            if (HEX_PATTERN.test(value)) this.applyColor(value);
        };
        hexInput.addEventListener('blur', commitHex);
        hexInput.addEventListener('keydown', event => {
            if (event.key === 'Enter') {
                event.preventDefault();
                commitHex();
            }
        });
        customRow.appendChild(hexInput);

        const nativeInput = document.createElement('input');
        nativeInput.type = 'color';
        nativeInput.className = 'ql-color-palette-native';
        nativeInput.value = HEX_PATTERN.test(currentValue) && currentValue.length === 7 ? currentValue : '#000000';
        // 'input' fires on every drag tick while the native picker is open - closing the
        // dropdown there would tear it (and the still-open native picker) out from under
        // the user mid-drag. Only commit once the picker is actually closed.
        nativeInput.addEventListener('change', event => this.applyColor(event.target.value));
        customRow.appendChild(nativeInput);

        this.dropdown.appendChild(customRow);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'ql-color-palette-remove';
        removeButton.textContent = this.options.removeLabel || 'Reset';
        removeButton.addEventListener('mousedown', event => {
            event.preventDefault();
            this.applyColor(false);
        });
        this.dropdown.appendChild(removeButton);

        this.positionDropdown();
    }

    appendSwatchSection(label, colors, currentValue, strictGrid) {
        const labelElement = document.createElement('div');
        labelElement.className = 'ql-color-palette-label';
        labelElement.textContent = label;
        this.dropdown.appendChild(labelElement);

        const swatches = document.createElement('div');
        swatches.className = 'ql-color-palette-swatches';
        if (strictGrid) swatches.classList.add('ql-color-palette-swatches--grid');
        colors.forEach(color => {
            const swatch = document.createElement('button');
            swatch.type = 'button';
            swatch.className = 'ql-color-palette-swatch';
            swatch.style.backgroundColor = color;
            swatch.title = color;
            if (color.toLowerCase() === String(currentValue).toLowerCase()) swatch.classList.add('active');
            swatch.addEventListener('mousedown', event => {
                event.preventDefault();
                this.applyColor(color);
            });
            swatches.appendChild(swatch);
        });
        this.dropdown.appendChild(swatches);
    }

    positionDropdown() {
        if (!this.dropdown || !this.savedSelection) return;

        const bounds = this.quill.getBounds(this.savedSelection.index);
        const editorRect = this.quill.root.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = editorRect.left + bounds.left;
        let top = editorRect.top + bounds.top + bounds.height + 5;

        const dropdownRect = this.dropdown.getBoundingClientRect();

        if (left + dropdownRect.width > viewportWidth - 10) {
            left = Math.max(10, viewportWidth - dropdownRect.width - 10);
        }
        if (top + dropdownRect.height > viewportHeight - 10) {
            top = Math.max(10, editorRect.top + bounds.top - dropdownRect.height - 5);
        }

        this.dropdown.style.left = left + 'px';
        this.dropdown.style.top = top + 'px';
    }

    applyColor(value) {
        const selection = this.savedSelection;
        const format = this.activeFormat;
        if (!selection || !format) return;

        this.quill.focus();
        this.quill.setSelection(selection.index, selection.length, Quill.sources.SILENT);
        this.quill.format(format, value, Quill.sources.USER);

        this.hideDropdown();
    }

    onMouseDown(event) {
        if (!this.dropdown) return;
        if (this.dropdown.contains(event.target)) return;

        const button = this.quill.container.parentNode.querySelector(`.ql-${this.activeFormat}`);
        if (button && button.contains(event.target)) return;

        this.hideDropdown();
    }

    hideDropdown() {
        if (this.dropdown) {
            this.dropdown.remove();
            this.dropdown = null;
        }
        this.activeFormat = null;
        this.savedSelection = null;
    }

    destroy() {
        document.removeEventListener('mousedown', this.boundOnMouseDown);
        this.hideDropdown();
    }
}

ColorPaletteModule.DEFAULTS = {
    ciColors: [],
};

export default ColorPaletteModule;
